import { useEffect, useState } from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import useNotification from "../../hooks/notification.hook";
import ModalContainer from "./ModalContainer";
import { useTranslation } from "react-i18next";
import { cn } from "../../lib/utils";
import { createTemplatePreset, editTemplatePreset } from "../../services/presets.service";

export default function CrudPresetPopup({
	open,
	setOpen,
	presetModel,
	onEdit = () => {},
	onInsert = () => {},
	templateId,
}) {
	const { t } = useTranslation();

	// Set message
	yup.setLocale({
		// use constant translation keys for messages without values
		mixed: {
			required: ({ path }) => t("common:errors.required", { field: t(`common:fields.${path}`) }),
		},
		number: {
			moreThan: ({ path, more }) =>
				t("common:errors.more_than", {
					field: t(`common:fields.${path}`),
					value: more,
				}),
			lessThan: ({ path, less }) =>
				t("common:errors.less_than", {
					field: t(`common:fields.${path}`),
					value: less,
				}),
		},
	});

	// Schema Yup
	const schema = yup.object().shape({
		name: yup.string().required(),
	});

	const {
		register,
		setValue,
		clearErrors,
		handleSubmit,
		formState: { errors },
	} = useForm({ resolver: yupResolver(schema) });

	const { openNotification, helperErrorNotification } = useNotification();

	const [loading, setLoading] = useState(false);

	/**
	 * Form Handler
	 * @param {form modal} data
	 */
	async function formHandler(data) {
		if (!loading) {
			setLoading(true);

			if (!presetModel?.id) {
				const body = {
					templateId: templateId,
					name: data.name,
					options: {
						organizationRequirementsStatuses: [],
						templateChapters: [],
						templateChapterContents: [],
						templateGoals: [],
						templateRequirements: [],
						deadlines: false,
						deadline_date_from: null,
						deadline_date_to: null,
						hide_approval_section: true,
						show_only_last_approval: false,
					},
				};

				// Create preset
				createTemplatePreset(
					body,
					(res) => {
						setLoading(false);
						openNotification({
							text: "Configurazione creata con successo!",
							status: "success",
						});
						onInsert(res.data);
						setOpen(false);
					},
					(error) => {
						helperErrorNotification(error);
						setLoading(false);
					}
				);
			} else {
				const body = {
					id: presetModel.id,
					name: data.name,
				};

				// Edit preset
				editTemplatePreset(
					body,
					(res) => {
						setLoading(false);
						openNotification({
							text: "Configurazione modificata con successo!",
							status: "success",
						});
						onEdit(res.data);
						setOpen(false);
					},
					(error) => {
						helperErrorNotification(error);
						setLoading(false);
					}
				);
			}
		}
	}

	/**
	 * Errors Form
	 */
	useEffect(() => {
		if (Object.keys(errors).length) {
			const fields = Object.values(errors).map((er) => er.message);
			openNotification({ fields, status: "error" });
		}
	}, [errors]);

	/**
	 * Init value
	 */
	useEffect(() => {
		if (open) {
			clearErrors();
			setValue("name", presetModel?.name || "");
		}
	}, [open]);

	return (
		<ModalContainer isOpen={open} setIsOpen={setOpen}>
			<div className={"main_modal_content"}>
				<button
					className="main_close"
					onClick={() => setOpen(false)}
					type="button"
					aria-label="Chiudi"
				>
					<span className="icon_10 default close icon_mask !bg-white"></span>
				</button>

				<div className="main_header_modal left_text">
					<span className="title">
						{presetModel?.id ? "Modifica configurazione" : "Nuova configurazione"}
					</span>
				</div>

				{/* Modal Form */}
				<div className={"main_body_modal"}>
					<form
						id="crud-preset-form"
						className="global_form_landing"
						onSubmit={handleSubmit(formHandler)}
						noValidate
					>
						{/* Name */}
						<div className="base">
							<div className="wrap_input_landing">
								<label htmlFor="crud-preset-name" className="label_input">
									Nome
								</label>
								<input
									className={cn("landing big", errors.name && "err")}
									id="crud-preset-name"
									type="text"
									{...register("name")}
								/>
							</div>
						</div>
					</form>
				</div>

				<div className="main_footer_modal">
					<div className="right_content">
						{/* Main Cta */}
						<button type="submit" form="crud-preset-form" className="button fill success">
							Conferma
						</button>
					</div>
				</div>
			</div>
		</ModalContainer>
	);
}
